import { useState } from "react";
import { IconMail, IconPlus } from "@tabler/icons-react";
import { toast } from "sonner";
import { ContactPerson, CustomerOrganization } from "../../utils/interfaces";
import {
  useCreateContactMutation,
  useUpdateCustomerRepresentativeDescriptionMutation,
  useUpdateCustomerEmailMutation,
  useCustomerAccountingDetailsQuery,
} from "../../api/queries/customerOrganizationQueries";
import { CustomerContactRow } from "./CustomerContactRow";
import { ContactPositionLevelSelector } from "./ContactPositionLevelSelector";
import { CustomerEmailConfirmationModal } from "./CustomerEmailConfirmationModal";
import { getApiErrorMessage } from "../../utils/apiError";
import { useAuth } from "../../hooks/useAuth";

interface CustomerContactsSectionProps {
  customer: CustomerOrganization;
  onCustomerUpdate?: () => void;
}

export const CustomerContactsSection = ({
  customer,
  onCustomerUpdate,
}: CustomerContactsSectionProps) => {
  const { canMutateOrg } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPositionCode, setNewPositionCode] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPrimaryEmail, setPendingPrimaryEmail] = useState<string | null>(
    null,
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const createMutation = useCreateContactMutation();
  const updateDescriptionMutation =
    useUpdateCustomerRepresentativeDescriptionMutation();
  const updateEmailMutation = useUpdateCustomerEmailMutation();

  const { data: accountingDetails, isLoading: isLoadingAccountingDetails } =
    useCustomerAccountingDetailsQuery(customer.id, {
      enabled: showConfirmModal,
    });

  const contacts = customer.contacts ?? [];

  const handleToggleAutoSending = async (checked: boolean) => {
    try {
      await updateDescriptionMutation.mutateAsync({
        customerRepId: customer.id,
        language: customer.language,
        is_template_sending: checked,
      });
      toast.success(checked ? "Auto sending enabled" : "Auto sending disabled");
      onCustomerUpdate?.();
    } catch {
      toast.error("Failed to update auto sending settings");
    }
  };

  const resetAddForm = () => {
    setNewName("");
    setNewEmail("");
    setNewPhone("");
    setNewPositionCode(null);
    setShowAddForm(false);
  };

  const handleAddContact = async () => {
    const name = newName.trim();
    const email = newEmail.trim();
    if (!name || !email.includes("@")) return;
    try {
      await createMutation.mutateAsync({
        orgId: customer.id,
        name,
        email,
        phone_number: newPhone.trim() || null,
        receives_automated_emails: false,
        position_code: newPositionCode,
      });
      resetAddForm();
      onCustomerUpdate?.();
      toast.success("Contact added");
    } catch {
      toast.error("Failed to add contact");
    }
  };

  const handlePrimaryEmailEdit = (contact: ContactPerson) => {
    setPendingPrimaryEmail(contact.email);
    setShowConfirmModal(true);
  };

  const handleConfirmPrimaryEmail = async () => {
    setIsConfirming(true);
    try {
      await updateEmailMutation.mutateAsync({
        customerRepId: customer.id,
        email: pendingPrimaryEmail,
      });
      setIsSuccess(true);
      setTimeout(() => {
        setShowConfirmModal(false);
        setIsSuccess(false);
        setIsConfirming(false);
        onCustomerUpdate?.();
      }, 1500);
    } catch (error) {
      setIsConfirming(false);
      toast.error("Failed to update primary email", {
        description: getApiErrorMessage(
          error,
          "Could not update the email in your accounting software.",
        ),
      });
    }
  };

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <IconMail size={18} className="text-slate-500" />
            <span className="text-sm font-medium uppercase tracking-[0.08em] text-slate-600">
              Contacts
            </span>
          </div>
          <div className="flex items-center gap-4">
            {canMutateOrg ? (
              <>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                  <span>Send templates</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={customer.is_template_sending}
                    onClick={() =>
                      handleToggleAutoSending(!customer.is_template_sending)
                    }
                    disabled={updateDescriptionMutation.isPending}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                      customer.is_template_sending
                        ? "bg-[#4B7F73]"
                        : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                        customer.is_template_sending
                          ? "translate-x-5"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddForm((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#4B7F73] hover:text-[#3F6C61]"
                >
                  <IconPlus size={16} /> Add contact
                </button>
              </>
            ) : (
              <span className="text-sm text-slate-500">
                {customer.is_template_sending
                  ? "Send templates on"
                  : "Send templates off"}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3 bg-white px-5 py-6">
          {contacts.length === 0 ? (
            <p className="text-sm text-slate-500">No contacts yet.</p>
          ) : (
            contacts.map((contact) => (
              <CustomerContactRow
                key={contact.id}
                contact={contact}
                org={customer}
                onPrimaryEmailEdit={handlePrimaryEmailEdit}
                onUpdated={onCustomerUpdate}
              />
            ))
          )}

          {contacts.length === 1 && !showAddForm && canMutateOrg && (
            <p className="text-xs text-slate-500">
              Add contacts and assign escalation position levels (Pos 2, 3, …)
              so external escalation steps know who to email.
            </p>
          )}

          {showAddForm && canMutateOrg && (
            <div className="space-y-2 rounded-xl border border-dashed border-slate-300 p-4">
              <p className="text-sm font-medium text-slate-700">New contact</p>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Email"
                type="email"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Phone (optional)"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <ContactPositionLevelSelector
                value={newPositionCode}
                compact
                disabled={createMutation.isPending}
                onChange={setNewPositionCode}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetAddForm}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddContact}
                  disabled={
                    createMutation.isPending ||
                    !newName.trim() ||
                    !newEmail.includes("@")
                  }
                  className="rounded-md bg-[#4B7F73] px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {createMutation.isPending ? "Saving…" : "Save contact"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <CustomerEmailConfirmationModal
        opened={showConfirmModal}
        onClose={() => {
          if (!isConfirming && !isSuccess) setShowConfirmModal(false);
        }}
        onConfirm={handleConfirmPrimaryEmail}
        accountingCustomerId={accountingDetails?.id ?? null}
        accountingCustomerName={accountingDetails?.name ?? null}
        previousEmail={accountingDetails?.email ?? null}
        newEmail={pendingPrimaryEmail ?? ""}
        isLoading={isLoadingAccountingDetails}
        isSuccess={isSuccess}
        isConfirming={isConfirming}
        accountingSoftware={accountingDetails?.accounting_software ?? null}
      />
    </>
  );
};
